<template>
  <div>
    <section class="main">
      <div :class="['headerSection', { headerSectionZoomed: zoom > 2 }]">
        <button id="openAllStagesButton" @click="openAllStages">
          {{ openingAllStages ? '⌛' : '↘' }}
        </button>
        <button id="closeAllStagesButton" @click="closeAllStages">
          {{ closingAllStages ? '⌛' : '→' }}
        </button>
        <button
          id="addStageToProjectButton"
          @click="addStageToProject(currentProject.children)"
          :disabled="!editingAllowed"
        >
          <b-icon icon="plus" scale="1.5" />
        </button>
        <button id="toggleEditShowState" @click="toggleEditShowState" style="float: right">
          <b-icon :icon="editShowStateIcon" />
        </button>
      </div>
      <div ref="partTitles" :class="['partTitles', { partTitlesZoomed: zoom > 2 }]" @scroll="handleTitleScroll">
        <div>
          <draggable
            @change="childMoved"
            :move="checkMove"
            v-model="stages"
            :options="{ group: 'categories', handle: '.glyphicon-move' }"
          >
            <div class="stage" v-for="(stage, index) in stages" :key="'title' + stage._id">
              <StageTitle
                v-if="stage.children"
                :showHiddenStages="editShowState"
                :lineheight="lineheight"
                :lockedDragNDrop="false"
                :level="0"
                :children="stage.children"
                :stage="fetchStage(stage._id)"
                :filterDate="filterDate"
                :filterDateEnd="filterDateEnd"
                :filterDateZoom="zoom"
              />
            </div>
          </draggable>
          <div v-if="canGetDiaryEntries" class="stage">
            <DiaryTitle :lineheight="lineheight" />
          </div>
        </div>
      </div>
      <div class="titles" :style="{ left: titlePosition }">
        <div
          class="monthTitle"
          :class="{ activePeriod: isActiveMonth(month) }"
          :style="{ left: monthPos(month), width: monthWidth(month) }"
          v-for="month in shownMonths"
          :key="'m' + month"
        >
          {{ getMonthDisplay(month) }}
        </div>
        <br />
        <div
          class="weekTitle"
          :class="{ activePeriod: isActiveWeek(week) }"
          :style="{ left: weekPos(week), width: weekWidth }"
          v-for="week in shownWeeks"
          :key="'w' + week"
        >
          <span :style="{ fontSize: weekFontSize }" :title="getDateForWeek(week)">
            {{ getWeekDisplay(week) }}
          </span>
        </div>
        <br />
        <div
          v-if="dayWidth > 10"
          class="dayTitle"
          :class="[dayHighLight(day)]"
          :style="{ left: dayPos(day), height: dayWidth + 'px', lineHeight: dayWidth + 'px' }"
          v-for="day in shownWeeks * 7"
          :key="'d' + day"
        >
          {{ getDayDisplay(day) }}
        </div>
      </div>
      <div
        ref="outerParts"
        id="outerParts"
        :class="['outerParts', { outerPartsZoomed: zoom > 2 }]"
        :style="[editingChild ? { right: '0px' } : viewDiaryEntry ? { right: '0px' } : '']"
        @scroll="handleScroll"
      >
        <div id="innerParts" :style="{ width: shownWeeksWidth }">
          <div class="stage" v-for="(stage, index) in stages" :key="stage._id">
            <Stage
              :lineheight="lineheight"
              :treeChild="stage"
              :showAlerts="showAlerts"
              v-if="stage.children"
              :showHiddenStages="editShowState"
              :lockedDragNDrop="false"
              :level="0"
              :children="stage.children"
              :stage="fetchStage(stage._id)"
              :filterDate="filterDate"
              :filterDateEnd="filterDateEnd"
              :filterDateZoom="zoom"
            />
          </div>
          <div v-if="canGetDiaryEntries" class="diary">
            <div class="child" :style="{ height: lineheight + 'px' }">
              <DiaryPart
                v-for="(item, index) in currentProjectDiaryEntries"
                :key="item._id"
                :lineheight="lineheight"
                :lockedDragNDrop="true"
                :level="0"
                :diaryEntry="item"
                :filterDate="filterDate"
                :filterDateEnd="filterDateEnd"
                :filterDateZoom="zoom"
              />
            </div>
          </div>
        </div>
      </div>
      <transition name="fade">
        <div class="editChild" :class="{ diary: viewDiaryEntry }" v-if="editingChild">
          <div>
            <PartEdit :filterDate="filterDate" :filterDateEnd="filterDateEnd" :part="selectedPart" v-if="editingPart" />
            <StageEdit :stage="selectedStage" v-if="editingStage" />
            <DiaryView
              :diaryEntry="selectedDiaryEntry"
              :projectCity="currentProject.address.city"
              v-if="viewDiaryEntry"
            />
          </div>
        </div>
      </transition>

      <context-menu id="context-menu-part" ref="ctxPartMenu">
        <div @click="embedPartInStage({ part: clickedPartData.part })" v-if="hasSimplePermissions('embedPartInStage')">
          <b-icon icon="plus" scale="1.5" />
          {{ $t('Split') }} (^ + ⏎)
        </div>
        <hr />
        <div @click="deletePart({ part: clickedPartData.part })" v-if="hasSimplePermissions('deletePart')">
          <b-icon icon="trash-fill" />
          {{ $t('Delete Part') }}
        </div>
      </context-menu>

      <context-menu id="context-menu-stage" ref="ctxStageMenu">
        <div @click="openAllChildrenStages">
          <b-icon icon="arrow-down" />
          {{ $t('Open All Stages') }}
        </div>
        <div @click="addStageToStage" v-if="hasSimplePermissions('addStage')">
          <b-icon icon="plus" scale="1.5" />
          {{ $t('Add Stage') }}
        </div>
        <div @click="addPartToStage" v-if="hasSimplePermissions('addStage')">
          <b-icon icon="plus" scale="1.5" />
          {{ $t('Add Part') }} (⇧ + ⏎)
        </div>

        <div @click="duplicateStage({ stage: clickedStageData.stage })" v-if="hasSimplePermissions('duplicateStage')">
          <b-icon icon="files" flip-h />
          {{ $t('Duplicate Stage') }}
        </div>

        <hr />
        <div
          @click="$modal.show('confirmDeleteDialog', { title: 'Delete Stage?' })"
          v-if="hasSimplePermissions('deleteStage')"
        >
          <b-icon icon="trash-fill" />
          {{ $t('Delete Stage') }}
        </div>
      </context-menu>
      <modal
        name="confirmDeleteDialog"
        classes="modalBoon"
        transition="nice-modal-fade"
        :clickToClose="false"
        :delay="100"
        :width="450"
      >
        <h2>{{ $t('Delete Stage?') }}</h2>
        <p>
          {{
            $t('Are you sure you want to delete the stage {title}?', {
              title: clickedStageData.stage ? clickedStageData.stage.title : '',
            })
          }}
        </p>
        <p>{{ $t('This action cannot be undone!') }}</p>
        <ul class="modalButtons">
          <li>
            <button class="darkButton" @click="confirmedDeleteStage(clickedStageData.stage)">
              {{ $t('Confirm') }}
            </button>
          </li>
          <li>
            <button class="lightButton" @click="$modal.hide('confirmDeleteDialog')">{{ $t('Cancel') }}</button>
          </li>
        </ul>
      </modal>
    </section>
  </div>
</template>
<script>
import { EventBus } from '@/common/eventbus';
import { fuzzytime } from '@/common/timeHelpers';
import { mapActions, mapGetters } from 'vuex';
import * as colourHelpers from '@/common/colourHelpers';
import contextMenu from 'vue-context-menu';
import draggable from 'vuedraggable';
import moment from 'moment';
import PartEdit from './PartEdit';
import Stage from './Stage';
import DiaryPart from './DiaryPart';
import DiaryView from './DiaryView';
import StageEdit from './StageEdit';
import StageTitle from './StageTitle';
import DiaryTitle from './DiaryTitle';

export default {
  name: 'ProjectPlanEdit',
  props: {
    lineheight: {
      type: Number,
      default: 20,
    },
    zoom: {
      type: Number,
      default: 1,
    },
    filterText: {
      type: String,
      default: '',
    },
    filterDate: {
      type: String,
      default: '2017-01-01',
    },
    filterDateEnd: {
      type: String,
      default: '2017-12-31',
    },
    filterDeliveries: {
      type: Boolean,
      default: false,
    },
    filterManagerId: {
      type: String,
      default: '',
    },
    showAlerts: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      clickedPartData: {},
      clickedStageData: {},
      closingAllStages: false,
      confirmDeleteDialog: false,
      openingAllStages: false,
      scrollLeft: 0,
      scrollTop: 0,
      syncingLScroll: false,
      syncingRScroll: false,
      editShowState: false,
    };
  },
  mounted: async function () {
    // this.selectPart(null);
    // await this.loadProject(this.$route.params._id);
    // this.filterDate = this.currentProject.projectStartdate;
    // this.filterDateEnd = this.currentProject.projectEnddate;
    this.loadSelectedChild();
    this.updateZoomFactor();

    // this.$nextTick(() => {
    //   window.addEventListener('resize', this.updateZoomFactor);
    //   // this.updateZoomFactor();
    //
    // });

    EventBus.$on('openPartContextMenu', ({ event, data }) => {
      this.clickedPartData = data;
      this.$refs.ctxStageMenu.ctxVisible = false;
      this.$refs.ctxPartMenu.open(event);
    });
    EventBus.$on('openStageContextMenu', ({ event, data }) => {
      this.clickedStageData = data;
      this.$refs.ctxPartMenu.ctxVisible = false;
      this.$refs.ctxStageMenu.open(event);
    });
  },
  beforeDestroy() {
    // window.removeEventListener('resize', this.updateZoomFactor);
    EventBus.$off('openPartContextMenu');
    EventBus.$off('openStageContextMenu');
  },
  methods: {
    ...mapActions([
      'closeAllTopLevelStages',
      'addPart',
      'addStage',
      'openAllTopLevelStages',
      'deletePart',
      'deleteStage',
      'duplicateStage',
      'embedPartInStage',
      'loadProject',
      'lockProject',
      'projectChildMoved',
      'saveProjectProperties',
      'selectPart',
      'selectStage',
      'selectDiaryEntry',
      'setViewSetting',
      'showAllChildren',
      'toggleStageChildren',
    ]),
    confirmedDeleteStage: function (stage) {
      this.$modal.hide('confirmDeleteDialog');
      this.deleteStage({ stage });
    },
    loadSelectedChild: function () {
      if (this.currentProject && this.currentProject.type === 'Project') {
        var route = this.$router.currentRoute;
        switch (route.name) {
          case 'ProjectPlanPart':
            if (this.canSelectPart && (!this.selectedPart || this.selectedPart._id !== route.params.partId)) {
              var part = this.fetchPart(route.params.partId);
              this.selectPart(part);
              this.$nextTick(() => {
                EventBus.$emit('focusPart', part);
              });
            }
            break;
          case 'ProjectPlanStage':
            if (this.canSelectStage && (!this.selectedStage || this.selectedStage._id !== route.params.stageId)) {
              this.selectStage(this.fetchStage(route.params.stageId));
            }
            break;
          case 'ProjectPlanDiary':
            if (
              this.canSelectDiaryEntry &&
              (!this.selectedDiaryEntry || this.selectedDiaryEntry.diaryDate !== route.params.date)
            ) {
              this.selectDiaryEntry(route.params.date);
            }
            break;
          case 'ProjectPlanEdit':
          case 'ProjectPlanPrint':
            if (this.canSelectDiaryEntry) {
              this.selectDiaryEntry(null);
            }
            if (this.canSelectPart) {
              this.selectPart(null);
            }
            if (this.canSelectStage) {
              this.selectStage(null);
            }
            break;
        }
      }
    },
    toggleEditShowState: function () {
      this.editShowState = !this.editShowState;
    },
    addStageToStage: function () {
      if (!this.clickedStageData.stage.showChildren) {
        this.toggleStageChildren(this.clickedStageData.stage);
      }

      this.addStage({ parent: this.clickedStageData.stage })
        .then((newStage) => {
          EventBus.$emit('focusStage', newStage);
        })
        .catch((e) => {
          console.error(e);
        });
    },
    addPartToStage: function () {
      if (!this.clickedStageData.stage.showChildren) {
        this.toggleStageChildren(this.clickedStageData.stage);
      }
      this.addPart({
        parent: this.clickedStageData.stage,
        newPartProperties: {
          startdate: this.clickedStageData.startdate,
          colour: colourHelpers.shadeColour(this.clickedStageData.stage.colour, 0.4),
        },
      }).then((part) => {
        if (this.canSelectPart) {
          this.selectPart(part);
          EventBus.$emit('focusPart', part);
        }
      });
    },
    closeAllStages: function () {
      this.closingAllStages = true;
      setTimeout(() => {
        this.closeAllTopLevelStages();
        this.closingAllStages = false;
      }, 100);
    },
    openAllStages: function () {
      this.openingAllStages = true;
      setTimeout(() => {
        this.openAllTopLevelStages();
        this.openingAllStages = false;
      }, 100);
    },
    openAllChildrenStages: function () {
      this.openingAllStages = true;
      setTimeout(() => {
        this.showAllChildren(this.clickedStageData.stage);
        this.openingAllStages = false;
      }, 100);
    },
    checkMove: function (e) {
      return this.projectDraggingAllowed;
    },
    childMoved: function (e) {
      console.log('projectPlan:childMoved', e, this.stages);

      if (e.removed) {
        return;
      }
      e.parent = this.currentProject;

      var newIndex = 0;
      var movedChild = {};
      if (e.moved) {
        movedChild = e.moved.element;
        newIndex = e.moved.newIndex;
        if (e.moved.newIndex > e.moved.oldIndex) {
          newIndex++;
        }
      } else {
        movedChild = e.added.element;
        newIndex = e.added.newIndex;
      }

      var position;
      if (newIndex < Object.keys(this.stages).length) {
        position = this.stages[newIndex];
      }
      console.log('stage:childMoved', movedChild.title, 'to above', position, 'in project ', this.currentProject.title);
      this.projectChildMoved({ movedChild, parent: this.currentProject, beforeChild: position });
      return false;
    },
    addStageToProject: function (parents) {
      this.addStage({ parent: parents, properties: { title: this.$t('New Category') } })
        .then((newStage) => {
          EventBus.$emit('focusStage', newStage);
        })
        .catch((e) => {
          console.error(e);
        });
    },
    getDateForWeek: function (showWeek) {
      var firstDay = moment(this.filterDate)
        .isoWeekday(1)
        .add(showWeek - 1, 'week');
      return (
        this.$t('w.{week}', { week: firstDay.isoWeek() }) +
        ' ' +
        firstDay.format('YYYY-MM-DD') +
        ' - ' +
        firstDay.isoWeekday(7).format('YYYY-MM-DD')
      );
    },
    isActiveWeek: function (checkWeek) {
      var week = moment(this.filterDate)
        .isoWeekday(1)
        .add(checkWeek - 1, 'week');
      return moment().isSame(week, 'week');
    },
    getWeekDisplay: function (showWeek) {
      var week = moment(this.filterDate)
        .isoWeekday(1)
        .add(showWeek - 1, 'week')
        .isoWeek();
      if (this.dayWidth > 3) {
        return this.$t('w.{week}', { week: week });
      } else if (this.dayWidth > 1.9) {
        return week;
      }

      return week % 5 === 0 || week === 1 ? week : '';
    },
    dayPos: function (shownDay) {
      var x = (shownDay - 1) * this.dayWidth;
      var diff = 0;
      return x + diff + 'px';
    },
    weekPos: function (shownWeek) {
      var x = (shownWeek - 1) * 7 * this.dayWidth;
      var day = moment(this.filterDate).day() - 1;
      if (day < 0) {
        day += 7;
      }
      var diff = 1 * this.zoom * this.viewSettings.projectPlan.zoomFactor * -day;

      return x + diff + 'px';
    },
    monthPos: function (shownMonth) {
      var dateOfShownMonth = moment(this.filterDate)
        .add(shownMonth - 1, 'month')
        .date(1); // .format('YYYY-MM-DD'); //.date(1);
      var x = this.dayWidth * moment(dateOfShownMonth).diff(this.filterDate, 'days');
      return x + 'px';
    },
    isActiveMonth: function (checkMonth) {
      var month = moment(this.filterDate).add(checkMonth - 1, 'month');
      return moment().isSame(month, 'month');
    },
    getDayDisplay: function (shownDay) {
      var day = moment(this.filterDate)
        .add(shownDay - 1, 'day')
        .format('DD');
      return day;
    },
    dayHighLight: function (shownDay) {
      var day = moment(this.filterDate).add(shownDay - 1, 'day');
      if (moment().isSame(day, 'day')) {
        return 'activeDay';
      }
      if (day.day() === 0 || day.day() === 6) {
        return 'dayHighLightWeekEnd';
      }
      return '';
    },
    getMonthDisplay: function (shownMonth) {
      var month = moment(this.filterDate).add(shownMonth - 1, 'month');
      // console.log('shownmont', month);
      if (this.dayWidth > 3) {
        return this.$t(month.format('MMMM')) + ' ' + month.year();
      } else if (this.dayWidth > 0.8) {
        return this.$t(month.format('MMM')) + ' ' + month.format('YY');
      }
      return this.$t(month.format('MMM'));
    },
    monthWidth: function (month) {
      var size =
        moment(this.filterDate)
          .add(month - 1, 'month')
          .daysInMonth() * this.dayWidth;
      return size + 'px';
    },
    updateTitle: function (e) {
      this.saveProjectProperties({
        updateProject: this.currentProject,
        newProjectProperties: { title: e.target.value },
      });
    },
    fuzzytime,

    updateZoomFactor: function () {
      var newZoomFactor = Math.round(((window.innerWidth - 250) / this.shownWeeks / 7) * 10) / 10;
      // var newZoomFactor = Math.round(10 * ((this.$refs.outerParts ? this.$refs.outerParts.offsetWidth : 1500) - 250) / this.shownWeeks / 7) / 10;
      // console.log('newZoomFactor', newZoomFactor);
      if (newZoomFactor !== this.viewSettings.projectPlan.zoomFactor) {
        this.$nextTick(function () {
          this.setViewSetting({
            section: 'projectPlan',
            property: 'zoomFactor',
            newValue: newZoomFactor,
          });
        });
      }
    },
    handleTitleScroll: function (e) {
      if (!this.syncingLScroll) {
        this.syncingRScroll = true;
        this.$refs.outerParts.scrollTop = e.target.scrollTop;
      }
      this.syncingLScroll = false;
    },
    locktime: function () {
      return this.fuzzytime(this.currentProject.lockedForEditingAt);
    },
    handleScroll: function (e) {
      this.scrollLeft = e.target.scrollLeft;
      this.scrollTop = e.target.scrollTop;
      if (!this.syncingRScroll) {
        this.syncingLScroll = true;
        this.$refs.partTitles.scrollTop = this.scrollTop;
      }
      this.syncingRScroll = false;
    },
  },
  computed: {
    ...mapGetters([
      'currentProject',
      'projectDraggingAllowed',
      'editingAllowed',
      'fetchPart',
      'getPersonByName',
      'fetchStage',
      'hasPropertyPermissions',
      'hasSimplePermissions',
      'lockedForEditingByCurrentUser',
      'selectedDiaryEntry',
      'selectedPart',
      'selectedStage',
      'filterParts',
      'username',
      'viewSettings',
      'currentProjectDiaryEntries',
    ]),

    canGetDiaryEntries() {
      return this.hasSimplePermissions('getDiaryEntries');
    },

    editShowStateIcon: function () {
      return this.editShowState ? 'check-square' : 'square';
    },
    stages: {
      get: function () {
        return this.filterParts({
          filterText: this.filterText,
          filterDate: this.filterDate,
          filterDateEnd: this.filterDateEnd,
          partManagerId: this.filterManagerId,
          filterDeliveries: this.filterDeliveries,
        });
      },
      set: function (value) {
        console.log('project plan set list', value);
      },
    },
    dayWidth: function () {
      var size = this.zoom * this.viewSettings.projectPlan.zoomFactor;
      return size;
    },
    shownWeeks: function () {
      return -moment(this.filterDate).isoWeekday(1).diff(moment(this.filterDateEnd).isoWeekday(1), 'weeks') + 1;
    },
    weekWidth: function () {
      var size = 7 * this.dayWidth;
      return size - 1 + 'px';
    },
    weekFontSize: function () {
      if (this.dayWidth < 3.5) {
        return '8px';
      }
      return '10px';
    },
    shownWeeksWidth: function () {
      return 200 + this.shownWeeks * 7 * this.dayWidth + 'px';
    },
    shownMonths: function () {
      return -moment(this.filterDate).diff(this.filterDateEnd, 'months') + 1;
    },
    canSelectPart() {
      return this.hasSimplePermissions('selectPart');
    },
    canSelectStage() {
      return this.hasSimplePermissions('selectStage');
    },
    canSelectDiaryEntry() {
      return this.hasSimplePermissions('selectDiaryEntry');
    },
    editingChild: function () {
      return (
        (this.editingPart && this.canSelectPart) || (this.editingStage && this.canSelectStage) || this.viewDiaryEntry
      );
    },
    editingPart: function () {
      return this.selectedPart != null;
    },
    editingStage: function () {
      return this.selectedStage != null;
    },
    /**
     * view diary page.
     */
    viewDiaryEntry: function () {
      return this.selectedDiaryEntry != null;
    },
    titlePosition: function () {
      var origin = 250 - this.scrollLeft;

      return origin /* + diff*/ + 'px';
    },
    partTitlesPosition: function () {
      var origin = -this.scrollTop;

      return origin /* + diff*/ + 'px';
    },
    projectIcon: function () {
      if (this.lockedForEditing && this.lockedForEditingByCurrentUser) {
        return 'lock_outline';
      }
      if (this.lockedForEditing && !this.lockedForEditingByCurrentUser) {
        return 'lock';
      }
      return '';
    },
    lockedDragNDrop: {
      get: function () {
        return this.currentProject.lockedDragNDrop;
      },
      set: function (value) {
        this.saveProjectProperties({
          updateProject: this.currentProject,
          newProjectProperties: { lockedDragNDrop: value },
        });
      },
    },
    unlockableByCurrentUser: function () {
      return (
        this.currentProject.lockedForEditingBy === this.username ||
        moment().diff(this.currentProject.lockedForEditingAt, 'minute') > 60
      );
    },
    lockedForEditing: {
      get: function () {
        return this.currentProject.lockedForEditingBy;
      },
      set: function (value) {
        this.lockProject(value);
      },
    },
  },
  components: {
    contextMenu,
    draggable,
    PartEdit,
    Stage,
    StageEdit,
    StageTitle,
    DiaryPart,
    DiaryTitle,
    DiaryView,
  },
  watch: {
    filterDate: function () {
      this.updateZoomFactor();
    },
    filterDateEnd: function () {
      this.updateZoomFactor();
    },
    currentProject: function () {
      this.loadSelectedChild();
    },
    $route: async function (to, from) {
      console.debug('ProjectPlanEdit:watch', 'query', JSON.stringify(to.query));
      this.loadSelectedChild();
    },
  },
};
</script>

<style scoped>
.main {
  background-color: white;
  position: fixed;
  bottom: 0px;
  /* Header's height is 80px; subheader's height is 3.375rem */
  top: calc(80px + 3.375rem);
  left: 250px;
  right: 0px;
  font-weight: 600;
}

h1 {
  font-size: 120%;
  text-align: left;
  margin: 0px;
}

.filterText {
  float: right;
  border-radius: 30px;
  text-indent: 1em;
}

.headerSection {
  display: block;
  margin: 0px;
  vertical-align: top;
  font-size: 80%;
  font-weight: bold;
  left: 0px;
  z-index: 3;
  position: fixed;
  width: 250px;
  height: 40px;
  background: #0f161f;
  border-right: 2px solid black;
}

.headerSectionZoomed {
  height: 52px;
}

.title {
  width: 160px;
  text-overflow: ellipsis;
  z-index: 0;
}

.duration {
  width: 40px;
  text-align: right;
  padding-right: 1em;
}
.startdate {
  width: 70px;
  text-align: right;
}

.editChild {
  background-color: white;
  width: 310px;
  right: 30px;
  position: fixed;
  bottom: 0px;
  /* top: 200px; */
  height: calc(100vh - 200px);
  border-radius: 10px;
  z-index: 1;

  overflow-y: scroll;
  /* overflow-x: auto; */
  border-left: 0px solid black;
  color: black;
  padding: 10px 0px;
  box-shadow: 0px 0px 60px rgba(0, 0, 0, 0.5);

  font-family: PoppinsRegular, sans-serif;
  letter-spacing: 0;
}
.editChild.diary {
  background-color: #091d2f;
  width: 350px;
  margin-top: 0;
}
.sortable-ghost {
  border: 2px dotted grey;
}

.partTitles {
  position: fixed;
  top: 174px;
  left: 0px;
  bottom: 0px;
  overflow-y: scroll;
  overflow-x: visible;
  width: 250px;
  z-index: 2;
  background: #0f161f;
  border-right: 2px solid black;
  padding-bottom: 8px; /* Should match scrollbars height! */
}

.partTitlesZoomed {
  top: 170px;
}

.partTitles::-webkit-scrollbar {
  background: white;
  display: none;
}

.outerParts {
  top: 174px;
  left: 250px;
  right: 0px;
  bottom: 0px;
  overflow: scroll;
  position: fixed;
  border: 0px solid red;
  z-index: 1;
  background: transparent;
  color: #051623;
  cursor: crosshair;
}

.outerPartsZoomed {
  top: 170px;
}

.titles {
  white-space: nowrap;
  font-size: 0.75rem;
  background: white;
  position: fixed;
  width: 100%;
  z-index: 0;
  color: black;
}

.monthTitle {
  display: block;
  position: absolute;
  padding-left: 2px;
  border-left: 1px solid #aaaaaa;
  height: 15px;
  text-overflow: hidden;
}

.weekTitle {
  display: block;
  position: absolute;
  padding-left: 2px;
  border-left: 1px solid #aaaaaa;
  height: 2500px;
  text-overflow: hidden;
}

.weekTitle:hover {
  background: #eee;
}

.dayTitle {
  display: block;
  position: absolute;
  border-bottom: 1px solid #bbb;
  width: 2500px;
  transform-origin: 0% 0%;
  transform: rotate(90deg) translateY(-100%);
  text-overflow: hidden;
  overflow: hidden;
  font-size: 10px;
  vertical-align: bottom;
}

.dayTitle:hover {
  background: #eee;
}

.dayHighLightWeekEnd {
  background: #fde5c5;
}

.partTitles .child {
  border: none;
}
.partTitles .child.selected {
  background: transparent;
}

.dragSpace {
  height: 5px;
}

.activePeriod {
  background: #eee;
}

.activeDay {
  background: #ddd;
}
#innerParts .diary {
  margin-left: 0;
  position: relative;
}
.diary {
  margin: 10px 0px 0px 0px;
}
.diary > .child {
  border-bottom: 1px solid #aaaaaa;
  z-index: 100;
  height: 20px;
}
</style>
<style>
.child:hover .glyphicon-move {
  color: white;
  transition-duration: 0.3s;
  transition-timing-function: ease-in-out;
}
.diaryContainer .weather-info {
  flex-direction: column;
  margin-top: 0;
  border-bottom: 0;
  border-top: 1px solid #253747;
}
.diaryContainer .weather-date {
  line-height: 40px;
  padding-bottom: 0;
}
.diaryContainer .weather-details {
  border-top: 1px solid #253747;
  margin-bottom: 0;
  width: auto;
  padding-top: 5px;
}
.diaryContainer .weather-details .left-side p {
  width: 60px;
}
.diaryContainer .weather-details .left-side p,
.diaryContainer .weather-details .right-side p {
  font-size: 10px;
}
.diaryContainer .weather-info .right-side {
  padding-left: 0;
}
.diaryContainer .weather-info .right-side div:nth-child(0) {
  margin-left: 0;
}
.diaryContainer .weather-info .right-side div:nth-child(2) {
  width: 75px;
}
.diaryContainer .weather-info .right-side div:nth-child(3) {
  width: 100px;
}
.diaryContainer .not-found {
  border-top: 1px solid #253747;
  border-bottom: 1px solid #253747;
  align-self: unset !important;
}
</style>



// WEBPACK FOOTER //
// src/components/projectplan/ProjectPlanEdit.vue